import type {
  AdminUser,
  AuditEntry,
  CatalogMapperView,
  ConnectionStatus,
  CorrigoChecklist,
  CorrigoDocumentType,
  CorrigoImportResult,
  CorrigoPushResult,
  CorrigoSearchResponse,
  CorrigoStepUpdateInput,
  Customer,
  DocumentUploadResult,
  InternalLineItem,
  LocationMatchConfig,
  LocationMatchInspectResult,
  LocationMatchSummary,
  MapperView,
  MappingImportResult,
  ParsedQuote,
  PasswordPolicy,
  PlatformId,
  PriceListItem,
  QuoteLineItem,
  QuoteLinePreview,
  QuoteSubmitInput,
  QuoteSubmitResult,
  SessionInfo,
  SubmissionsReport,
  UserRole,
  WorkOrderDocumentTargets,
  WorkOrderSearchMatch,
  WorkOrderSummary,
} from '@tcg/shared';

/** Error thrown for any non-2xx API response, carrying the server's user message. */
export class ApiClientError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = 'ApiClientError';
  }
}

let csrfToken: string | null = null;

async function ensureCsrf(): Promise<string> {
  if (csrfToken) return csrfToken;
  const res = await fetch('/api/csrf', { credentials: 'include' });
  const data = (await res.json()) as { csrfToken: string };
  csrfToken = data.csrfToken;
  return csrfToken;
}

async function parseError(res: Response): Promise<never> {
  let code = 'error';
  let message = `Request failed (${res.status})`;
  try {
    const body = (await res.json()) as { error?: { code: string; userMessage: string } };
    if (body.error) {
      code = body.error.code;
      message = body.error.userMessage;
    }
  } catch {
    /* non-JSON error */
  }
  throw new ApiClientError(code, message);
}

async function jsonRequest<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET') headers['x-csrf-token'] = await ensureCsrf();

  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  async me(): Promise<SessionInfo> {
    return jsonRequest('GET', '/api/auth/me');
  },
  async login(email: string, password: string): Promise<SessionInfo> {
    return jsonRequest('POST', '/api/auth/login', { email, password });
  },
  async logout(): Promise<void> {
    await jsonRequest('POST', '/api/auth/logout');
  },
  async changePassword(currentPassword: string, newPassword: string): Promise<SessionInfo> {
    return jsonRequest('POST', '/api/auth/change-password', { currentPassword, newPassword });
  },
  async connections(): Promise<ConnectionStatus[]> {
    const data = await jsonRequest<{ connections: ConnectionStatus[] }>('GET', '/api/connections');
    return data.connections;
  },
  serviceChannelConnectUrl(): string {
    return '/api/connections/servicechannel/connect';
  },
  async disconnectServiceChannel(): Promise<void> {
    await jsonRequest('POST', '/api/connections/servicechannel/disconnect');
  },

  async parsePdf(file: File): Promise<ParsedQuote> {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch('/api/quote/parse', {
      method: 'POST',
      credentials: 'include',
      headers: { 'x-csrf-token': await ensureCsrf() },
      body: form,
    });
    if (!res.ok) await parseError(res);
    return (await res.json()) as ParsedQuote;
  },

  async searchWorkOrders(platform: PlatformId, woNumber: string): Promise<WorkOrderSearchMatch[]> {
    const q = new URLSearchParams({ platform, woNumber });
    const d = await jsonRequest<{ matches: WorkOrderSearchMatch[] }>(
      'GET',
      `/api/quote/work-orders/search?${q.toString()}`,
    );
    return d.matches;
  },

  async getWorkOrder(
    platform: PlatformId,
    woNumber: string,
    workOrderId?: number,
  ): Promise<WorkOrderSummary> {
    const q = new URLSearchParams({ platform, woNumber });
    if (workOrderId != null) q.set('workOrderId', String(workOrderId));
    return jsonRequest('GET', `/api/quote/work-order?${q.toString()}`);
  },

  async getPriceList(platform: PlatformId): Promise<PriceListItem[]> {
    const q = new URLSearchParams({ platform });
    const data = await jsonRequest<{ items: PriceListItem[] }>(
      'GET',
      `/api/quote/price-list?${q.toString()}`,
    );
    return data.items;
  },

  async previewLines(
    platform: PlatformId,
    woNumber: string,
    lineItems: Array<{
      description: string;
      quantity: number;
      isTaxable?: boolean;
      unitPrice?: number;
      descriptionDetail?: string | null;
    }>,
    workOrderId?: number,
  ): Promise<QuoteLinePreview> {
    return jsonRequest('POST', '/api/quote/line-preview', {
      platform,
      woNumber,
      workOrderId,
      lineItems,
    });
  },

  async submitQuote(input: QuoteSubmitInput & { platform: PlatformId }): Promise<QuoteSubmitResult> {
    return jsonRequest('POST', '/api/quote/submit', input);
  },

  async history(): Promise<AuditEntry[]> {
    const data = await jsonRequest<{ entries: AuditEntry[] }>('GET', '/api/quote/history');
    return data.entries;
  },
};

/** Admin-only Price List Mapping endpoints. */
export const adminApi = {
  async listCustomers(): Promise<Customer[]> {
    const d = await jsonRequest<{ customers: Customer[] }>('GET', '/api/admin/price-list/customers');
    return d.customers;
  },
  async refreshCustomers(): Promise<number> {
    const d = await jsonRequest<{ count: number }>('POST', '/api/admin/price-list/customers/refresh');
    return d.count;
  },
  async addCustomer(customerId: string, name: string): Promise<void> {
    await jsonRequest('POST', '/api/admin/price-list/customers', { customerId, name });
  },
  async refreshPriceList(
    customerId: string,
    workOrderId?: number,
  ): Promise<{ count: number; noWorkOrder: boolean }> {
    return jsonRequest(
      'POST',
      `/api/admin/price-list/${encodeURIComponent(customerId)}/refresh`,
      workOrderId != null ? { workOrderId } : {},
    );
  },
  async getMapper(customerId: string): Promise<MapperView> {
    return jsonRequest('GET', `/api/admin/price-list/${encodeURIComponent(customerId)}/mapper`);
  },
  /** Canonical Corrigo-item-centric mapper (one row per Corrigo price-list item). */
  async getCatalog(customerId: string): Promise<CatalogMapperView> {
    return jsonRequest('GET', `/api/admin/price-list/${encodeURIComponent(customerId)}/catalog`);
  },
  async listInternalItems(): Promise<InternalLineItem[]> {
    const d = await jsonRequest<{ items: InternalLineItem[] }>('GET', '/api/admin/internal-items');
    return d.items;
  },
  async createInternalItem(input: {
    code: string;
    name: string;
    defaultUnit?: string;
    taxable?: boolean | null;
  }): Promise<InternalLineItem> {
    return jsonRequest('POST', '/api/admin/internal-items', input);
  },
  /** Update a Facil-IT item's name/unit or activate/deactivate it. */
  async updateInternalItem(
    id: string,
    patch: { name?: string; defaultUnit?: string | null; active?: boolean },
  ): Promise<void> {
    await jsonRequest('PATCH', `/api/admin/internal-items/${encodeURIComponent(id)}`, patch);
  },
  async saveMapping(
    customerId: string,
    input: {
      internalLineItemId: string;
      priceListItemId: string;
      overrideRate?: number | null;
      notes?: string | null;
    },
  ): Promise<void> {
    await jsonRequest('PUT', `/api/admin/price-list/${encodeURIComponent(customerId)}/mappings`, input);
  },
  /** Set/clear a Corrigo item's Facil-IT mapping (null clears). */
  async setItemMapping(
    customerId: string,
    input: { priceListItemId: string; internalLineItemId: string | null; overrideRate?: number | null },
  ): Promise<void> {
    await jsonRequest(
      'PUT',
      `/api/admin/price-list/${encodeURIComponent(customerId)}/catalog/mappings`,
      input,
    );
  },
  async deleteMapping(customerId: string, internalLineItemId: string): Promise<void> {
    await jsonRequest(
      'DELETE',
      `/api/admin/price-list/${encodeURIComponent(customerId)}/mappings/${encodeURIComponent(
        internalLineItemId,
      )}`,
    );
  },
  /** Download the catalog+mappings CSV (or mappings JSON) for the customer. */
  async exportCsv(customerId: string): Promise<void> {
    const res = await fetch(`/api/admin/price-list/${encodeURIComponent(customerId)}/export.csv`, {
      credentials: 'include',
    });
    if (!res.ok) await parseError(res);
    saveBlob(await res.blob(), filenameFromResponse(res, `mappings_${customerId}.csv`));
  },
  async exportJson(customerId: string): Promise<void> {
    const data = await jsonRequest(
      'GET',
      `/api/admin/price-list/${encodeURIComponent(customerId)}/export.json`,
    );
    saveBlob(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }), `mappings_${customerId}.json`);
  },
  /** Upload a mapping CSV; preview (apply=false) or apply. */
  async importCsv(
    customerId: string,
    file: File,
    opts: { apply: boolean; addMissing?: boolean },
  ): Promise<MappingImportResult> {
    const q = new URLSearchParams({
      apply: String(opts.apply),
      addMissing: String(opts.addMissing ?? false),
    });
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(
      `/api/admin/price-list/${encodeURIComponent(customerId)}/import?${q.toString()}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'x-csrf-token': await ensureCsrf() },
        body: form,
      },
    );
    if (!res.ok) await parseError(res);
    return (await res.json()) as MappingImportResult;
  },
};

/**
 * Admin-only user & security management — backs the two-tier role model and
 * password administration. Every endpoint is gated server-side on the admin role.
 */
export const adminUsersApi = {
  async list(): Promise<AdminUser[]> {
    const d = await jsonRequest<{ users: AdminUser[] }>('GET', '/api/admin/users');
    return d.users;
  },
  /**
   * Create a user. When `password` is omitted the server generates a one-time
   * temporary password and returns it ONCE in `tempPassword` (null when an
   * explicit password was supplied). The user is forced to change it on first login.
   */
  async create(input: {
    email: string;
    role: UserRole;
    password?: string;
  }): Promise<{ user: AdminUser; tempPassword: string | null }> {
    return jsonRequest('POST', '/api/admin/users', input);
  },
  /** Reset a user's password to a fresh one-time temp password (returned once). */
  async resetPassword(id: string): Promise<{ user: AdminUser; tempPassword: string }> {
    return jsonRequest('POST', `/api/admin/users/${encodeURIComponent(id)}/reset-password`);
  },
  async setRole(id: string, role: UserRole): Promise<AdminUser | null> {
    const d = await jsonRequest<{ user: AdminUser | null }>(
      'PATCH',
      `/api/admin/users/${encodeURIComponent(id)}/role`,
      { role },
    );
    return d.user;
  },
  async remove(id: string): Promise<void> {
    await jsonRequest('DELETE', `/api/admin/users/${encodeURIComponent(id)}`);
  },
  async getPasswordPolicy(): Promise<PasswordPolicy> {
    const d = await jsonRequest<{ policy: PasswordPolicy }>(
      'GET',
      '/api/admin/settings/password-policy',
    );
    return d.policy;
  },
  async setPasswordPolicy(policy: PasswordPolicy): Promise<PasswordPolicy> {
    const d = await jsonRequest<{ policy: PasswordPolicy }>(
      'PUT',
      '/api/admin/settings/password-policy',
      policy,
    );
    return d.policy;
  },
};

/** Admin-only reporting endpoints. */
export const adminReportsApi = {
  /**
   * Quote and checklist submissions per day over the inclusive `[from, to]` day
   * range (both `YYYY-MM-DD`). `tz` defaults to the browser's timezone so day
   * buckets match the admin's local clock; the response is gap-filled across the
   * whole range.
   */
  async submissions(from: string, to: string, tz?: string): Promise<SubmissionsReport> {
    const q = new URLSearchParams({ from, to });
    q.set('tz', tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
    const d = await jsonRequest<{ report: SubmissionsReport }>(
      'GET',
      `/api/admin/reports/submissions?${q.toString()}`,
    );
    return d.report;
  },
};

/** CorrigoPro Checklist tool endpoints. */
export const corrigoApi = {
  async status(): Promise<{ configured: boolean }> {
    return jsonRequest('GET', '/api/corrigo/status');
  },
  async search(
    number: string,
    opts?: { from?: string; to?: string; field?: 'Created' | 'LastUpdate' },
  ): Promise<CorrigoSearchResponse> {
    const q = new URLSearchParams({ number });
    if (opts?.from) q.set('from', opts.from);
    if (opts?.to) q.set('to', opts.to);
    if (opts?.field) q.set('field', opts.field);
    return jsonRequest('GET', `/api/corrigo/work-orders/search?${q.toString()}`);
  },
  async getChecklist(workOrderId: number): Promise<CorrigoChecklist> {
    return jsonRequest('GET', `/api/corrigo/work-orders/${workOrderId}`);
  },
  async pushSteps(
    workOrderId: number,
    body: { workOrderNumber: string; steps: CorrigoStepUpdateInput[] },
  ): Promise<CorrigoPushResult> {
    return jsonRequest('PUT', `/api/corrigo/work-orders/${workOrderId}/procedure-steps`, body);
  },
  /** Fetch the vendor PDF (cookie-auth) and trigger a browser download. */
  async downloadVendorPdf(workOrderId: number, filename: string): Promise<void> {
    const res = await fetch(`/api/corrigo/work-orders/${workOrderId}/vendor-pdf`, {
      credentials: 'include',
    });
    if (!res.ok) await parseError(res);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  /** Upload a filled vendor PDF; get back step responses to review and push. */
  async importVendorPdf(workOrderId: number, file: File): Promise<CorrigoImportResult> {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`/api/corrigo/work-orders/${workOrderId}/import-pdf`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'x-csrf-token': await ensureCsrf() },
      body: form,
    });
    if (!res.ok) await parseError(res);
    return (await res.json()) as CorrigoImportResult;
  },
};

/**
 * Corrigo document upload. Uploads use XHR (not fetch) so the UI can show
 * per-file progress; file metadata travels in the query string and the body
 * carries only the file. One request per file → a failed file can be retried
 * on its own without touching the others.
 */
export interface UploadOptions {
  documentType?: CorrigoDocumentType;
  /** General attachments only. */
  messageVisibility?: string;
  /** Required-document slot id (required-document uploads only). */
  documentId?: string;
  /** Required-document optional metadata. */
  documentDate?: string;
  description?: string;
}

function uploadFile(
  path: string,
  file: File,
  csrf: string,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<DocumentUploadResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', path);
    xhr.withCredentials = true;
    xhr.setRequestHeader('x-csrf-token', csrf);
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        reject(new ApiClientError('aborted', 'Upload cancelled'));
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as DocumentUploadResult);
        } catch {
          reject(new ApiClientError('bad_response', 'Unexpected server response'));
        }
        return;
      }
      let code = 'error';
      let message = `Upload failed (${xhr.status})`;
      try {
        const body = JSON.parse(xhr.responseText) as { error?: { code: string; userMessage: string } };
        if (body.error) {
          code = body.error.code;
          message = body.error.userMessage;
        }
      } catch {
        /* non-JSON error */
      }
      reject(new ApiClientError(code, message));
    };
    xhr.onerror = () => reject(new ApiClientError('network', 'Network error during upload'));
    xhr.onabort = () => reject(new ApiClientError('aborted', 'Upload cancelled'));
    const form = new FormData();
    form.append('file', file);
    xhr.send(form);
  });
}

export const documentsApi = {
  async status(): Promise<{ configured: boolean }> {
    return jsonRequest('GET', '/api/documents/status');
  },
  async searchWorkOrders(woNumber: string): Promise<WorkOrderSearchMatch[]> {
    const q = new URLSearchParams({ woNumber });
    const d = await jsonRequest<{ matches: WorkOrderSearchMatch[] }>(
      'GET',
      `/api/documents/work-orders/search?${q.toString()}`,
    );
    return d.matches;
  },
  async getTargets(woNumber: string, workOrderId?: number): Promise<WorkOrderDocumentTargets> {
    const q = new URLSearchParams({ woNumber });
    if (workOrderId != null) q.set('workOrderId', String(workOrderId));
    return jsonRequest('GET', `/api/documents/work-order?${q.toString()}`);
  },
  async uploadGeneral(
    woNumber: string,
    file: File,
    opts: UploadOptions & { workOrderId?: number } = {},
    onProgress?: (fraction: number) => void,
    signal?: AbortSignal,
  ): Promise<DocumentUploadResult> {
    const q = new URLSearchParams({ woNumber });
    if (opts.workOrderId != null) q.set('workOrderId', String(opts.workOrderId));
    if (opts.documentType) q.set('documentType', opts.documentType);
    if (opts.messageVisibility) q.set('messageVisibility', opts.messageVisibility);
    return uploadFile(`/api/documents/general?${q.toString()}`, file, await ensureCsrf(), onProgress, signal);
  },
  async uploadRequired(
    woNumber: string,
    documentId: string,
    file: File,
    opts: UploadOptions & { workOrderId?: number } = {},
    onProgress?: (fraction: number) => void,
    signal?: AbortSignal,
  ): Promise<DocumentUploadResult> {
    const q = new URLSearchParams({ woNumber, documentId });
    if (opts.workOrderId != null) q.set('workOrderId', String(opts.workOrderId));
    if (opts.documentType) q.set('documentType', opts.documentType);
    if (opts.documentDate) q.set('documentDate', opts.documentDate);
    if (opts.description) q.set('description', opts.description);
    return uploadFile(`/api/documents/required?${q.toString()}`, file, await ensureCsrf(), onProgress, signal);
  },
};

/**
 * Photo Manager — server-side HEIC→JPEG conversion. In-browser HEIC decoders are
 * unreliable, so HEIC/HEIF bytes are sent here and the server returns JPEG bytes.
 * Non-HEIC images are converted in the browser and never hit this endpoint.
 */
export const photosApi = {
  async convertHeicToJpeg(file: File): Promise<Blob> {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch('/api/photos/convert', {
      method: 'POST',
      credentials: 'include',
      headers: { 'x-csrf-token': await ensureCsrf() },
      body: form,
    });
    if (!res.ok) await parseError(res);
    return res.blob();
  },
};

/** Trigger a browser "Save As" for an already-fetched blob. */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Read the download filename from a Content-Disposition header (best effort). */
function filenameFromResponse(res: Response, fallback: string): string {
  const cd = res.headers.get('content-disposition') ?? '';
  const match = /filename="?([^"]+)"?/.exec(cd);
  return match ? match[1] : fallback;
}

/**
 * Data-export tool. Each export is a GET that streams a CSV; we fetch with the
 * session cookie and hand the browser a download.
 */
export const dataExportApi = {
  async status(): Promise<{ corrigo: boolean; servicechannel: boolean }> {
    return jsonRequest('GET', '/api/data-export/status');
  },
  async download(path: string, fallbackName: string): Promise<number> {
    const res = await fetch(path, { credentials: 'include' });
    if (!res.ok) await parseError(res);
    const rowCount = Number(res.headers.get('x-row-count') ?? '0');
    saveBlob(await res.blob(), filenameFromResponse(res, fallbackName));
    return rowCount;
  },
  corrigoLocations(branchId: string, customerId: string): Promise<number> {
    const q = new URLSearchParams({ branchId, customerId });
    return this.download(`/api/data-export/corrigo/locations?${q}`, `corrigo_locations_CLEAN_${customerId}.csv`);
  },
  corrigoTrades(branchId: string, customerId: string): Promise<number> {
    const q = new URLSearchParams({ branchId, customerId });
    return this.download(`/api/data-export/corrigo/trades?${q}`, `corrigo_trades_${customerId}.csv`);
  },
  corrigoPriorities(branchId: string, customerId: string): Promise<number> {
    const q = new URLSearchParams({ branchId, customerId });
    return this.download(`/api/data-export/corrigo/priorities?${q}`, `corrigo_priorities_${customerId}.csv`);
  },
  scLocations(subscriberId: string): Promise<number> {
    const q = new URLSearchParams({ subscriberId });
    return this.download(`/api/data-export/servicechannel/locations?${q}`, `${subscriberId}-locations.csv`);
  },
  scTrades(subscriberId: string): Promise<number> {
    const q = new URLSearchParams({ subscriberId });
    return this.download(`/api/data-export/servicechannel/trades?${q}`, `${subscriberId}-trades.csv`);
  },
  scPriorities(subscriberId: string): Promise<number> {
    const q = new URLSearchParams({ subscriberId });
    return this.download(`/api/data-export/servicechannel/priorities?${q}`, `${subscriberId}-priorities.csv`);
  },
};

/** Location Match tool — inspect a workbook, then run the reconciliation. */
export const locationMatchApi = {
  async inspect(file: File): Promise<LocationMatchInspectResult> {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch('/api/location-match/inspect', {
      method: 'POST',
      credentials: 'include',
      headers: { 'x-csrf-token': await ensureCsrf() },
      body: form,
    });
    if (!res.ok) await parseError(res);
    return (await res.json()) as LocationMatchInspectResult;
  },
  /** Runs the match; downloads the MATCHED workbook and returns the summary. */
  async run(file: File, config: LocationMatchConfig): Promise<LocationMatchSummary> {
    const form = new FormData();
    form.append('file', file);
    const q = new URLSearchParams({ config: JSON.stringify(config) });
    const res = await fetch(`/api/location-match/run?${q}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'x-csrf-token': await ensureCsrf() },
      body: form,
    });
    if (!res.ok) await parseError(res);
    const summaryHeader = res.headers.get('x-match-summary');
    const summary = summaryHeader ? (JSON.parse(summaryHeader) as LocationMatchSummary) : null;
    saveBlob(await res.blob(), filenameFromResponse(res, summary?.filename ?? 'MATCHED.xlsx'));
    if (!summary) throw new ApiClientError('bad_response', 'Match summary missing from response.');
    return summary;
  },
};

export type { QuoteLineItem };
